import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Stack, Grid, MenuItem } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import * as yup from "yup";
import moment from "moment-timezone";
import { yupResolver } from "@hookform/resolvers/yup";
import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgCheckbox from "../../../../CommonFormComp/MpgCheckbox";
import MpgItem from "../../../../CommonFormComp/MpgItem";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgSuccessDialog from "../../../../common/dialog/MpgSuccessDialog";

import {
  useGetLanguageListQuery,
  useGetTimezonesListQuery,
} from "../../../../reactQueries/commonRQHooks.js";
import { useCreateAdminMessageMutation } from "../../../../reactQueries/AppApi/rqHooksForAdminMessages";
import { adminMsgViewPath } from "../../../../routes/routePaths";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog.jsx";
import MpgAutocompleteSingle from "../../../../CommonFormComp/MpgAutocompleteSingle.jsx";
import { useGetSiteCoreCMSData } from "../../../../reactQueries/commonRQHooks.js";

const CreateAdminMessage = () => {
  let navigate = useNavigate();

  // get cms content
  const {
    isSuccess: isSuccessOnSiteCoreCMS,
    error: errorOnSiteCoreCMS,
    data: adminMessageCMSDataFromAPI,
    isError: isErrorOnSiteCoreCMS,
    isFetching: isFetchingOnSiteCoreCMS
  } = useGetSiteCoreCMSData({ pagename: "AdminMessages" });

  const [adminMessagesCMSData, setAdminMessagesCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} });

  const fetchAdminMessageCMSData = () => {
    if (adminMessageCMSDataFromAPI && adminMessageCMSDataFromAPI?.Pages) {
      adminMessageCMSDataFromAPI.Pages.map((el) => {
        if (el.Key === "AdminMessages") {
          const { Title, Description, TabNavigationTitle, Components } = el;
          setAdminMessagesCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
        }
      });
    }
  }

  useEffect(() => {
    fetchAdminMessageCMSData();
  }, [adminMessageCMSDataFromAPI]);

  const returnLabel = (labelArray=[], key) => {
      return labelArray.find((x) => x.Key === key)?.Phrase || '';
  }

  const fetchAdminMessageDetailsLabel = (key) => {
    if (adminMessagesCMSData && adminMessagesCMSData?.pageContent["AdminMessageDetailsComponent"]) {
      const adminMessagesCMSDataLabel = adminMessagesCMSData.pageContent["AdminMessageDetailsComponent"];
      return returnLabel(adminMessagesCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const schema = yup.object().shape({
    message_header: yup.string().label('This field').required(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')).max(200),
    content: yup.string().label('This field').required(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')).max(1300),
    persistent: yup.boolean().label('This field').default(false).optional(),
    timezone: yup.object().label('This field').required().typeError(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')),
    fromDate: yup
      .date()
      .label('This field')
      .required()
      .typeError(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')),
    toDate: yup
      .date()
      .label('This field')
      .required()
      .test(
        "from-less-than-to",
        fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField'),
        function (value) {
          const newTd = moment(value).toISOString();
          const newFd = moment(this.parent.fromDate).toISOString();

          if (newTd > newFd) {
            return true;
          } else {
            return false;
          }
        }
      )
      .typeError(fetchAdminMessageDetailsLabel('AdminMessageDetails.ValidationMsgRequiredField')),
  });

  /**get timezonesList */
  const {
    isSuccess,
    isFetching,
    error,
    data: timezonesList,
  } = useGetTimezonesListQuery();

  /** !!!!!!!! autocomplete option lists !!!!!!! */
  const timezonesAutocompleteList = !!timezonesList && timezonesList.map((item) => {
    return {
      id: item.Id,
      selectOptionLabel: item.MasterTimeZoneName,
      selectOptionValue: item.Id,
      extraData: item
    }
  })
  // console.log("timezones:", timezonesList)

  const { data: languagesList } = useGetLanguageListQuery();
  /**API calls to CreateAdminMessage */
  const {
    mutate: createAdminMessage,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    data: dataOnCreate,
    error: errorOnCreate,
  } = useCreateAdminMessageMutation();


  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fromDate: null,
      toDate: null,
    },
  });

  const {
    setValue,
    getValues,
    formState: { errors },
    watch
  } = rhfFormInstance;

  const selectedTimezoneDetails = watch('timezone')
  const selectedIANATimeZoneId = selectedTimezoneDetails?.extraData?.IANATimeZoneId


  const onSubmit = (data) => {
    /**we r using timezones so need to convert local time into UTC format to store in DB*/
    const toDate_inUTC = moment(data?.toDate).utc().toISOString()
    const fromDate_inUTC = moment(data?.fromDate).utc().toISOString()

    const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
      "name"
    ];
    const body = {
      MessageHeader: data?.message_header,
      MessageContent: data?.content,
      Persistent: data?.persistent,
      TimeZone: data?.timezone?.extraData?.MasterTimeZoneName,
      TimeZoneId: data?.timezone?.id,
      FromDate: fromDate_inUTC,
      ToDate: toDate_inUTC,
      CreatedBy: userName || "",
      Languages: languagesList,
      IsActive: true,
    };

    createAdminMessage({ body });
  };

  useEffect(() => {
    if (!!isSuccessOnCreate) {
      navigate(adminMsgViewPath);
    }
  }, [isSuccessOnCreate]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {adminMessagesCMSData?.pageContent["AdminMessageDetailsComponent"]?.Title}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={adminMsgViewPath}
        >
          {fetchAdminMessageDetailsLabel('AdminMessageDetails.AdminMessage')}
        </Button>
      </Stack>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <MpgItem sx={{ padding: { xs: "20px" } }}>
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {fetchAdminMessageDetailsLabel('AdminMessageDetails.NewMessageDetails')}
              </Typography>
              <MpgInput
                rhfFormInstance={rhfFormInstance}
                name={"message_header"}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.MessageHeader')}
                fullWidth
              />

              <MpgInput
                rhfFormInstance={rhfFormInstance}
                name={"content"}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.MessageContent')}
                placeholder={fetchAdminMessageDetailsLabel('AdminMessageDetails.AddMessageContentHere')}
                multiline
                rows={5}
                fullWidth
              />
              <div style={{ textAlign: "left" }}>
                <MpgCheckbox
                  rhfFormInstance={rhfFormInstance}
                  name={"persistent"}
                  label={fetchAdminMessageDetailsLabel('AdminMessageDetails.Persistent')}
                  fullWidth
                  sx={{ textAlign: "left" }}
                />
              </div>

              <MpgAutocompleteSingle
                rhfFormInstance={rhfFormInstance}
                name={'timezone'}
                label={fetchAdminMessageDetailsLabel('AdminMessageDetails.Timezones')}
                fullWidth
                optionList={timezonesAutocompleteList || []}
              />

              <Stack
                direction={{ sm: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                mt={2}
              >

                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    name="fromDate"
                    label={fetchAdminMessageDetailsLabel('AdminMessageDetails.From')}
                    disablePast
                    value={getValues("fromDate")}
                    onChange={(newValue) => {
                      setValue("fromDate", newValue, {
                        shouldValidate: true,
                      });
                    }}
                    timezone={!!selectedIANATimeZoneId ? selectedIANATimeZoneId : ''}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors["fromDate"],
                        helperText: errors["fromDate"]?.message,
                      },
                    }}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    disablePast
                    fullWidth
                    name="toDate"
                    label={fetchAdminMessageDetailsLabel('AdminMessageDetails.To')}
                    value={getValues("toDate")}
                    onChange={(newValue) => {
                      setValue("toDate", newValue, { shouldValidate: true });
                    }}
                    timezone={!!selectedIANATimeZoneId ? selectedIANATimeZoneId : ''}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors["toDate"],
                        helperText: errors["toDate"]?.message,
                      },
                    }}
                  />
                </LocalizationProvider>

              </Stack>
            </MpgItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MpgItem>
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {fetchAdminMessageDetailsLabel('AdminMessageDetails.Languages')}
              </Typography>

              {
                languagesList?.map((item, index) => {
                  return (
                    <div key={`${item}-${index}`} style={{ textAlign: "left" }}>
                      <MpgCheckbox
                        rhfFormInstance={rhfFormInstance}
                        name={`${item?.LanguageDescription}-lang`}
                        label={`${item?.LanguageDescription}`}
                        fullWidth
                        sx={{ textAlign: "left" }}
                        defaultChecked={true}
                        disabled
                      />
                    </div>
                  );
                })
              }
            </MpgItem>
          </Grid>
        </Grid>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={"space-evenly"}
          mt={3}
        >
          <Button variant="outlined" type="submit" size="small">
            {fetchAdminMessageDetailsLabel('AdminMessageDetails.CreateMessage')}
          </Button>
        </Stack>
      </Box>

      {/** error dialog on create action */}
      <MpgErrorDialog
        open={!!isErrorOnCreate}
      />
      <MpgLoadingDialog
        open={!!isLoadingOnCreate}
      />
    </Box>
  );
};

export default CreateAdminMessage;
